import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserGroupIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  ArchiveBoxIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import api from '../../utils/api';

const ConsignorsPage = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedConsignor, setSelectedConsignor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: ''
  });
  const [error, setError] = useState(null);
  
  // Fetch consignors
  const {
    data: consignors,
    isLoading,
    isError
  } = useQuery('consignors', async () => {
    const response = await api.get('/api/consignors');
    return response.data;
  });
  
  // Create consignor mutation
  const createConsignorMutation = useMutation(
    (data) => api.post('/api/consignors', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('consignors');
        resetForm();
        setIsCreating(false);
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to create consignor');
      }
    }
  );
  
  // Update consignor mutation
  const updateConsignorMutation = useMutation(
    ({ id, data }) => api.put(`/api/consignors/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('consignors');
        resetForm();
        setIsEditing(false);
        setSelectedConsignor(null);
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to update consignor');
      }
    }
  );
  
  // Delete consignor mutation
  const deleteConsignorMutation = useMutation(
    (id) => api.delete(`/api/consignors/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('consignors');
      },
      onError: (err) => {
        alert(err.response?.data?.message || 'Failed to delete consignor');
      }
    }
  );
  
  // Toggle consignor active status mutation
  const toggleStatusMutation = useMutation(
    ({ id, isActive }) => api.put(`/api/consignors/${id}`, { isActive }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('consignors');
      },
      onError: (err) => {
        alert(err.response?.data?.message || 'Failed to update consignor status');
      }
    }
  );
  
  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      code: ''
    });
    setError(null);
  };
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle form submission for creating a consignor
  const handleCreateSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      setError('Name and code are required');
      return;
    }
    
    createConsignorMutation.mutate(formData);
  };
  
  // Handle form submission for updating a consignor
  const handleUpdateSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      setError('Name and code are required');
      return;
    }
    
    updateConsignorMutation.mutate({
      id: selectedConsignor.id,
      data: formData
    });
  };
  
  // Initialize edit form with consignor data
  const handleEditConsignor = (consignor) => {
    setSelectedConsignor(consignor);
    setFormData({
      name: consignor.name,
      code: consignor.code
    });
    setIsEditing(true);
    setIsCreating(false);
  };
  
  // Handle delete consignor
  const handleDeleteConsignor = (consignor) => {
    if (consignor.itemCount > 0) {
      alert(`Cannot delete consignor ${consignor.name} (${consignor.code}) because they have ${consignor.itemCount} inventory items.`);
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete consignor ${consignor.name} (${consignor.code})?`)) {
      deleteConsignorMutation.mutate(consignor.id);
    }
  };
  
  // Handle toggle consignor active status
  const handleToggleStatus = (consignor) => {
    toggleStatusMutation.mutate({
      id: consignor.id,
      isActive: !consignor.isActive
    });
  };
  
  // Filter consignors based on search term and active status
  const filteredConsignors = consignors?.filter(consignor => {
    const matchesSearch = searchTerm === '' || 
      consignor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      consignor.code.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = showInactive || consignor.isActive;
    
    return matchesSearch && matchesStatus;
  });
  
  return (
    <div className="max-w-7xl mx-auto">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Consignors</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage your consignors and track inventory items associated with each person.
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            onClick={() => {
              resetForm();
              setIsCreating(true);
              setIsEditing(false);
            }}
            className="btn-primary"
          >
            <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
            Add Consignor
          </button>
        </div>
      </div>
      
      {/* Search and filter */}
      <div className="mt-6 flex flex-col sm:flex-row justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            className="form-input pl-10"
            placeholder="Search consignors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex items-center">
          <input
            id="showInactive"
            name="showInactive"
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            checked={showInactive}
            onChange={() => setShowInactive(!showInactive)}
          />
          <label htmlFor="showInactive" className="ml-2 block text-sm text-gray-900">
            Show inactive consignors
          </label>
        </div>
      </div>
      
      {/* Consignor form (create or edit) */}
      {(isCreating || isEditing) && (
        <div className="mt-6 bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {isCreating ? 'Add New Consignor' : 'Edit Consignor'}
            </h3>
            
            {error && (
              <div className="mt-4 rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={isCreating ? handleCreateSubmit : handleUpdateSubmit} className="mt-5 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              {/* Name and Code */}
              <div className="sm:col-span-3">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Consignor Name *
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="Enter consignor name"
                  />
                </div>
              </div>
              
              <div className="sm:col-span-3">
                <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                  Consignor Code *
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="code"
                    id="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="Enter unique code"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">This code will be included in exports and should be unique.</p>
              </div>
              
              <div className="sm:col-span-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setIsCreating(false);
                    setIsEditing(false);
                    setSelectedConsignor(null);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={createConsignorMutation.isLoading || updateConsignorMutation.isLoading}
                >
                  {createConsignorMutation.isLoading || updateConsignorMutation.isLoading ? (
                    'Saving...'
                  ) : (
                    isCreating ? 'Create Consignor' : 'Update Consignor'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Consignors - Card Style */}
      <div className="mt-6 bg-white shadow rounded-lg">
        {/* Column Headers */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center space-x-6">
            <div className="text-sm font-medium text-gray-700">Name</div>
            <div className="text-sm font-medium text-gray-700">Code</div>
            <div className="text-sm font-medium text-gray-700">Items</div>
            <div className="text-sm font-medium text-gray-700">Status</div>
          </div>
        </div>
        
        {/* Loading or No Items State */}
        {isLoading ? (
          <div className="py-10 text-center text-gray-500">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
            <p className="mt-2">Loading consignors...</p>
          </div>
        ) : isError ? (
          <div className="py-10 text-center text-red-500">
            <p>Error loading consignors. Please try again.</p>
          </div>
        ) : filteredConsignors?.length === 0 ? (
          <div className="py-10 text-center text-gray-500">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No consignors</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by adding a new consignor.
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setIsCreating(true);
                  setIsEditing(false);
                }}
                className="btn-primary"
              >
                <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                Add Consignor
              </button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredConsignors.map((consignor) => (
              <div key={consignor.id} className={`py-3 ${!consignor.isActive ? 'bg-gray-50' : ''}`}>
                {/* Consignor Row */}
                <div className="flex items-center justify-between py-2 px-4 hover:bg-gray-50 rounded-md">
                  <div className="flex items-center space-x-4">
                    {/* Name */}
                    <div className="min-w-[200px]">
                      <div className="text-sm font-medium text-gray-900">
                        {consignor.name}
                      </div>
                    </div>
                    
                    {/* Code */}
                    <div className="text-sm text-gray-500 min-w-[100px]">
                      <div className="flex items-center">
                        <TagIcon className="h-4 w-4 mr-1 text-gray-400" />
                        {consignor.code}
                      </div>
                    </div>
                    
                    {/* Items */}
                    <div className="text-sm text-gray-500 min-w-[100px]">
                      <Link 
                        to={`/app/inventory?consignorId=${consignor.id}`} 
                        className="flex items-center hover:text-primary-600"
                      >
                        <ArchiveBoxIcon className="h-4 w-4 mr-1 text-gray-400" />
                        {consignor.itemCount} items
                      </Link>
                    </div>
                    
                    {/* Status */}
                    <div className="min-w-[100px]">
                      {consignor.isActive ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                          <CheckCircleIcon className="mr-1 h-4 w-4" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                          <XCircleIcon className="mr-1 h-4 w-4" />
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex space-x-3">
                    {/* Edit button */}
                    <button
                      onClick={() => handleEditConsignor(consignor)}
                      className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                    >
                      <PencilIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                    
                    {/* Toggle status button */}
                    <button
                      onClick={() => handleToggleStatus(consignor)}
                      className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors"
                    >
                      {consignor.isActive ? (
                        <XCircleIcon className="h-4 w-4 hover:text-gray-700" aria-hidden="true" />
                      ) : (
                        <CheckCircleIcon className="h-4 w-4 hover:text-green-600" aria-hidden="true" />
                      )}
                    </button>
                    
                    {/* Delete button */}
                    <button
                      onClick={() => handleDeleteConsignor(consignor)}
                      className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      disabled={consignor.itemCount > 0}
                    >
                      <TrashIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsignorsPage;
